import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  CountryCode,
  CustomerOrderDetail,
  InvoiceKind,
  RefundDestination,
  RefundView,
  WalletView,
} from '@capsico/contracts';
import { errorMessage, errorRef } from '../api/client';
import { chooseRefund, downloadInvoice } from '../api/payments';
import { saveFile } from '../utils/gateway';
import { showToast } from './useToast';

/** How a refund looks right after the customer picks where it goes (before the server answers). */
function picked(refund: RefundView, destination: RefundDestination): RefundView {
  return {
    ...refund,
    destination,
    status: destination === 'wallet' ? 'done' : 'processing',
    chooseBy: null,
  };
}

/**
 * "Capsico Money now (+bonus)" or "back to the original method": the choice shows at once on the
 * order page and the wallet page, and is rolled back if the server refuses.
 */
export function useRefundChoice(
  scope: { orderId?: string; country?: CountryCode },
  messages: { saved: string },
) {
  const client = useQueryClient();
  const orderKey = ['my-order', scope.orderId ?? 'none'];
  const walletKey = ['wallet', scope.country ?? 'none'];

  const choose = useMutation({
    mutationFn: (input: { refundId: string; destination: RefundDestination }) =>
      chooseRefund(input.refundId, input.destination),
    onMutate: async ({ refundId, destination }) => {
      await Promise.all([
        client.cancelQueries({ queryKey: orderKey }),
        client.cancelQueries({ queryKey: walletKey }),
      ]);
      const order = client.getQueryData<CustomerOrderDetail>(orderKey);
      const wallet = client.getQueryData<WalletView>(walletKey);
      if (order) {
        client.setQueryData<CustomerOrderDetail>(orderKey, {
          ...order,
          payment: {
            ...order.payment,
            refunds: order.payment.refunds.map((refund) =>
              refund.id === refundId ? picked(refund, destination) : refund,
            ),
          },
        });
      }
      if (wallet) {
        const refund = wallet.pendingChoices.find((row) => row.id === refundId);
        client.setQueryData<WalletView>(walletKey, {
          ...wallet,
          balanceMinor:
            refund && destination === 'wallet'
              ? wallet.balanceMinor + refund.amountMinor + refund.bonusMinor
              : wallet.balanceMinor,
          pendingChoices: wallet.pendingChoices.filter((row) => row.id !== refundId),
        });
      }
      return { order, wallet };
    },
    onError: (failure, _input, context) => {
      if (context?.order) client.setQueryData(orderKey, context.order);
      if (context?.wallet) client.setQueryData(walletKey, context.wallet);
      showToast(errorMessage(failure), { tone: 'negative', ref: errorRef(failure) });
    },
    onSuccess: () => showToast(messages.saved, { tone: 'positive' }),
    onSettled: () => {
      if (scope.orderId) void client.invalidateQueries({ queryKey: orderKey });
      void client.invalidateQueries({ queryKey: ['wallet'] });
      void client.invalidateQueries({ queryKey: ['my-orders'] });
    },
  });

  return {
    choose: (refundId: string, destination: RefundDestination) =>
      choose.mutate({ refundId, destination }),
    choosingId: choose.isPending ? (choose.variables?.refundId ?? null) : null,
  };
}

/** Downloads a tax invoice PDF (food or Capsico fees) for one order. */
export function useInvoiceDownload(orderId: string, messages: { failed: string }) {
  const download = useMutation({
    mutationFn: (kind: InvoiceKind) => downloadInvoice(orderId, kind),
    onSuccess: ({ blob, fileName }) => saveFile(blob, fileName),
    onError: () => showToast(messages.failed, { tone: 'negative' }),
  });
  return {
    download: (kind: InvoiceKind) => download.mutate(kind),
    downloading: download.isPending ? (download.variables ?? null) : null,
  };
}
